import React, { useState, useEffect } from "react";
import { TwitterTweetEmbed } from "react-twitter-embed";
import {
  MDBSpinner,
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBTextArea,
  MDBInput,
} from "mdb-react-ui-kit";
import {
  getSimpleConfig,
  codiumCheckAuth,
  updateCodiumTweet,
} from "../../../utils/Client";

function QodoReply() {
  const [replyData, setReplyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedReply, setEditedReply] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [authError, setAuthError] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmIndex, setConfirmIndex] = useState(null);

  useEffect(() => {
    document.title = "Qodo | Automatic Tweets Management";
  }, []);

  const handleAuth = async () => {
    try {
      const isValid = await codiumCheckAuth(secretKey);
      if (isValid) {
        setIsAuthenticated(true);
        setAuthError("");
        fetchData();
      } else {
        setAuthError("Invalid secret key");
      }
    } catch (err) {
      setAuthError("Authentication failed");
    }
  };

  const fetchData = async () => {
    try {
      const response = await getSimpleConfig("codium-tweet-reply");
      const filteredReplies = response.filter((item) =>
        ["PENDING", "APPROVED", "REJECTED"].includes(item.status),
      );
      setReplyData(filteredReplies);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleApprove = async (index) => {
    setConfirmAction("approve");
    setConfirmIndex(index);
    setConfirmModal(true);
  };

  const handleReject = async (index) => {
    setConfirmAction("reject");
    setConfirmIndex(index);
    setConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    try {
      setUpdateLoading(true);
      const item = replyData[confirmIndex];
      const status = confirmAction === "approve" ? "APPROVED" : "REJECTED";
      await updateCodiumTweet(item.link, item.reply, status, secretKey);
      // Update local state
      const newReplyData = [...replyData];
      newReplyData[confirmIndex] = {
        ...newReplyData[confirmIndex],
        status: status
      };
      setReplyData(newReplyData);
    } catch (err) {
      setError(`Failed to ${confirmAction} tweet`);
    } finally {
      setUpdateLoading(false);
      setConfirmModal(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedReply(replyData[index].reply);
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      setUpdateLoading(true);
      const item = replyData[editingIndex];
      await updateCodiumTweet(item.link, editedReply, "PENDING", secretKey);
      // Update local state
      const newReplyData = [...replyData];
      newReplyData[editingIndex] = {
        ...newReplyData[editingIndex],
        reply: editedReply,
      };
      setReplyData(newReplyData);
      setEditModal(false);
    } catch (err) {
      setError("Failed to update reply");
    } finally {
      setUpdateLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div
        className="container-fluid"
        style={{
          padding: "2rem",
          maxWidth: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#1a1a1a",
        }}
      >
        <div
          style={{
            backgroundColor: "#2a2a2a",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 0 15px rgba(0, 136, 255, 0.5)",
            border: "1px solid rgba(0, 136, 255, 0.3)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <h4 className="text-center mb-4" style={{ color: "#fff" }}>
            Enter secretKey to continue
          </h4>
          <MDBInput
            label="Enter Secret Key"
            type="password"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAuth();
              }
            }}
            style={{
              backgroundColor: "#1a1a1a",
              color: "#fff",
              border: "1px solid rgba(0, 136, 255, 0.3)",
              marginBottom: "1rem",
            }}
            labelStyle={{ color: "#fff" }}
          />
          {authError && (
            <div className="text-danger mb-3" style={{ fontSize: "0.9rem" }}>
              {authError}
            </div>
          )}
          <MDBBtn color="primary" className="w-100" onClick={handleAuth}>
            Authenticate
          </MDBBtn>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center" style={{ padding: "2rem" }}>
        <MDBSpinner color="primary">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-danger">Error: {error}</div>;
  }

  return (
    <div
      className="container-fluid"
      style={{ padding: "2rem", maxWidth: "100%" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            backgroundColor: "#2a2a2a",
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            border: "1px solid rgba(0, 136, 255, 0.3)",
            boxShadow: "0 0 10px rgba(0, 136, 255, 0.2)",
          }}
        >
          <span style={{ color: "#0088ff", marginRight: "0.5rem" }}>
            <i className="fas fa-clock"></i>
          </span>
          <span style={{ color: "#fff" }}>
            {replyData.filter((item) => item.status === "PENDING").length}{" "}
            Pending Replies
          </span>
        </div>
        <h3 className="text-center mb-0" style={{ color: "#fff" }}>
          <span
            style={{
              backgroundColor: "#0088ff",
              padding: "0.3rem",
              borderRadius: "0.5rem",
              color: "#fff",
            }}
          >
            Qodo:
          </span>{" "}
          Manage automatic replies
        </h3>
        <div style={{ width: "200px" }}></div>
      </div>
      <div className="table-responsive" style={{ width: "100%" }}>
        <table
          className="table table-bordered"
          style={{
            color: "#fff",
            backgroundColor: "transparent",
            width: "100%",
            boxShadow: "0 0 15px rgba(0, 136, 255, 0.5)",
            border: "1px solid rgba(0, 136, 255, 0.3)",
            borderRadius: "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "5%",
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  padding: "1rem",
                  borderBottom: "2px solid rgba(0, 136, 255, 0.3)",
                }}
              >
                #
              </th>
              <th
                style={{
                  width: "45%",
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  padding: "1rem",
                  borderBottom: "2px solid rgba(0, 136, 255, 0.3)",
                }}
              >
                Tweet
              </th>
              <th
                style={{
                  width: "35%",
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  padding: "1rem",
                  borderBottom: "2px solid rgba(0, 136, 255, 0.3)",
                }}
              >
                Generated Reply
              </th>
              <th
                style={{
                  width: "15%",
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  padding: "1rem",
                  borderBottom: "2px solid rgba(0, 136, 255, 0.3)",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {replyData.map((item, index) => {
              const tweetId = item.link.split("/").pop();
              return (
                <tr key={index}>
                  <td
                    style={{
                      backgroundColor: "#1a1a1a",
                      padding: "1rem",
                      textAlign: "center",
                      verticalAlign: "middle",
                      border: "1px solid rgba(0, 136, 255, 0.2)",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      backgroundColor: "#1a1a1a",
                      padding: "1rem",
                      border: "1px solid rgba(0, 136, 255, 0.2)",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0088ff",
                          textDecoration: "none",
                          display: "block",
                          marginBottom: "1rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        <i
                          className="fab fa-twitter"
                          style={{ marginRight: "0.5rem" }}
                        ></i>
                        {item.link}
                      </a>
                      <TwitterTweetEmbed tweetId={tweetId} />
                    </div>
                  </td>
                  <td
                    style={{
                      backgroundColor: "#1a1a1a",
                      padding: "1rem",
                      border: "1px solid rgba(0, 136, 255, 0.2)",
                    }}
                  >
                    <div
                      className="p-3 rounded"
                      style={{
                        backgroundColor: "#2a2a2a",
                        width: "100%",
                        boxShadow: "0 0 10px rgba(0, 136, 255, 0.2)",
                      }}
                    >
                      {item.reply}
                    </div>
                  </td>
                  <td
                    style={{
                      backgroundColor: "#1a1a1a",
                      padding: "1rem",
                      textAlign: "center",
                      verticalAlign: "middle",
                      border: "1px solid rgba(0, 136, 255, 0.2)",
                    }}
                  >
                    {item.status === "PENDING" ? (
                      <div className="d-flex flex-column gap-2 align-items-center">
                        <MDBBtn
                          color="success"
                          size="sm"
                          onClick={() => handleApprove(index)}
                          disabled={updateLoading}
                        >
                          {updateLoading ? (
                            <MDBSpinner size="sm" color="light" />
                          ) : (
                            "Approve"
                          )}
                        </MDBBtn>
                        <MDBBtn
                          color="warning"
                          size="sm"
                          onClick={() => handleEdit(index)}
                          disabled={updateLoading}
                        >
                          Edit
                        </MDBBtn>
                        <MDBBtn
                          color="danger"
                          size="sm"
                          onClick={() => handleReject(index)}
                          disabled={updateLoading}
                        >
                          {updateLoading ? (
                            <MDBSpinner size="sm" color="light" />
                          ) : (
                            "Reject"
                          )}
                        </MDBBtn>
                        <br />
                        <span className="text-muted">
                          You can not edit after Approve/Reject
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span
                          style={{
                            color:
                              item.status === "APPROVED"
                                ? "#00C851"
                                : "#ff4444",
                            fontSize: "0.9rem",
                            fontWeight: "bold",
                          }}
                        >
                          {item.status}
                        </span>
                        <br />
                        {item.status === "APPROVED" && (
                          <span className="text-muted">
                            The reply will be tweeted soon
                          </span>
                        )}
                        {item.status === "REJECTED" && (
                          <span className="text-muted">
                            The reply was rejected. It will be deleted in 24
                            hours.
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <MDBModal show={editModal} setShow={setEditModal} tabIndex="-1">
        <MDBModalDialog size="lg">
          <MDBModalContent
            style={{ backgroundColor: "#1a1a1a", color: "#fff" }}
          >
            <MDBModalHeader
              style={{ borderBottom: "1px solid rgba(0, 136, 255, 0.3)" }}
            >
              <MDBModalTitle style={{ color: "#fff" }}>
                Edit Reply
              </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
              <div style={{ marginBottom: "1rem" }}>
                <span
                  style={{
                    color: editedReply.length > 260 ? "#ff4444" : "#fff",
                    fontSize: "0.9rem",
                  }}
                >
                  {editedReply.length}/260 characters
                </span>
              </div>
              <MDBTextArea
                label="Reply Text"
                id="replyText"
                rows={8}
                value={editedReply}
                onChange={(e) => {
                  if (e.target.value.length <= 260) {
                    setEditedReply(e.target.value);
                  }
                }}
                style={{
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  border: "1px solid rgba(0, 136, 255, 0.3)",
                  boxShadow: "0 0 10px rgba(0, 136, 255, 0.2)",
                  fontSize: "1.1rem",
                  padding: "1rem",
                }}
                labelStyle={{
                  color: "#fff",
                  fontSize: "1.2rem",
                  marginBottom: "1rem",
                }}
              />
            </MDBModalBody>
            <MDBModalFooter
              style={{ borderTop: "1px solid rgba(0, 136, 255, 0.3)" }}
            >
              <MDBBtn color="secondary" onClick={() => setEditModal(false)}>
                Cancel
              </MDBBtn>
              <MDBBtn
                color="primary"
                onClick={handleSaveEdit}
                disabled={editedReply.length > 260 || updateLoading}
              >
                {updateLoading ? (
                  <MDBSpinner size="sm" color="light" />
                ) : (
                  "Save Changes"
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MDBModal show={confirmModal} setShow={setConfirmModal} tabIndex="-1">
        <MDBModalDialog>
          <MDBModalContent style={{ backgroundColor: "#1a1a1a", color: "#fff" }}>
            <MDBModalHeader style={{ borderBottom: "1px solid rgba(0, 136, 255, 0.3)" }}>
              <MDBModalTitle style={{ color: "#fff" }}>
                Confirm Action
              </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
              <p style={{ color: "#fff", fontSize: "1.1rem" }}>
                Are you sure you want to {confirmAction} this tweet?
              </p>
            </MDBModalBody>
            <MDBModalFooter style={{ borderTop: "1px solid rgba(0, 136, 255, 0.3)" }}>
              <MDBBtn color="secondary" onClick={() => setConfirmModal(false)}>
                Cancel
              </MDBBtn>
              <MDBBtn 
                color={confirmAction === "approve" ? "success" : "danger"}
                onClick={handleConfirmAction}
                disabled={updateLoading}
              >
                {updateLoading ? (
                  <MDBSpinner size="sm" color="light" />
                ) : (
                  `Yes, ${confirmAction}`
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
}

export default QodoReply;
